import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

import { PipelineStepListItem, PipelineStepPack } from '../../../state/pipeline-editor.state';

export type DropZone = 'above' | 'on' | 'below';

@Component({
  selector: 'ts-pipeline-step-management-list',
  templateUrl: './pipeline-step-management-list.component.html',
  styleUrls: ['./pipeline-step-management-list.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule],
})
export class PipelineStepManagementListComponent {
  @Input() items: PipelineStepListItem[] = [];
  @Input() loading = true;
  @Input() disabled = false;

  @Output() addClicked = new EventEmitter<void>();
  @Output() editClicked = new EventEmitter<PipelineStepListItem>();
  @Output() visualizeClicked = new EventEmitter<PipelineStepListItem>();
  @Output() removeConfirmed = new EventEmitter<PipelineStepListItem>();
  @Output() reordered = new EventEmitter<PipelineStepListItem[]>();

  pendingRemovalUuid: string | null = null;
  draggedUuid: string | null = null;
  hoveredRowUuid: string | null = null;
  hoveredZone: DropZone | null = null;

  get packs(): PipelineStepPack[] {
    const result: PipelineStepPack[] = [];
    for (const item of this.items) {
      const last = result[result.length - 1];
      if (last && last.order === item.order) {
        last.items.push(item);
      } else {
        result.push({ order: item.order, items: [item] });
      }
    }
    return result;
  }

  trackByPackOrder(_: number, pack: PipelineStepPack): number {
    return pack.order;
  }

  trackByUuid(_: number, item: PipelineStepListItem): string {
    return item.uuid;
  }

  requestRemove(item: PipelineStepListItem): void {
    this.pendingRemovalUuid = item.uuid;
  }

  confirmRemove(item: PipelineStepListItem): void {
    this.pendingRemovalUuid = null;
    this.removeConfirmed.emit(item);
  }

  cancelRemove(): void {
    this.pendingRemovalUuid = null;
  }

  onDragStart(item: PipelineStepListItem, event: DragEvent): void {
    if (this.disabled || this.pendingRemovalUuid !== null) {
      event.preventDefault();
      return;
    }
    this.draggedUuid = item.uuid;
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', item.uuid);
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onDragOver(item: PipelineStepListItem, event: DragEvent): void {
    if (this.disabled || this.draggedUuid === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.hoveredRowUuid = item.uuid;
    this.hoveredZone = this.computeZone(event.clientY - rect.top, rect.height);
  }

  onDragEnd(): void {
    this.draggedUuid = null;
    this.hoveredRowUuid = null;
    this.hoveredZone = null;
  }

  onDrop(item: PipelineStepListItem, event: DragEvent): void {
    event.preventDefault();
    if (this.disabled || this.draggedUuid === null) {
      this.onDragEnd();
      return;
    }
    const draggedUuid = this.draggedUuid;
    const zone = this.hoveredZone ?? 'on';
    this.onDragEnd();
    this.dropOn(draggedUuid, item.uuid, zone);
  }

  dropOn(draggedUuid: string, targetUuid: string, zone: DropZone): void {
    if (this.disabled) return;
    if (draggedUuid === targetUuid) return;

    const next = this.items.map((i) => ({ ...i }));
    const dragged = next.find((i) => i.uuid === draggedUuid);
    const target = next.find((i) => i.uuid === targetUuid);
    if (!dragged || !target) return;

    if (zone === 'above') {
      const insertAt = target.order;
      for (const i of next) {
        if (i.uuid !== draggedUuid && i.order >= insertAt) i.order += 1;
      }
      dragged.order = insertAt;
    } else if (zone === 'below') {
      const insertAt = target.order + 1;
      for (const i of next) {
        if (i.uuid !== draggedUuid && i.order >= insertAt) i.order += 1;
      }
      dragged.order = insertAt;
    } else {
      dragged.order = target.order;
    }

    this.reordered.emit(this.renormalize(next));
  }

  isZoneActive(item: PipelineStepListItem, zone: DropZone): boolean {
    return (
      this.draggedUuid !== null &&
      this.draggedUuid !== item.uuid &&
      this.hoveredRowUuid === item.uuid &&
      this.hoveredZone === zone
    );
  }

  isPackZoneActive(pack: PipelineStepPack): boolean {
    return pack.items.some((i) => this.isZoneActive(i, 'on'));
  }

  private computeZone(offsetY: number, height: number): DropZone {
    const t = height > 0 ? offsetY / height : 0.5;
    if (t < 0.3) return 'above';
    if (t > 0.7) return 'below';
    return 'on';
  }

  private renormalize(items: PipelineStepListItem[]): PipelineStepListItem[] {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const orderMap = new Map<number, number>();
    let nextOrder = 0;
    for (const item of sorted) {
      if (!orderMap.has(item.order)) {
        orderMap.set(item.order, nextOrder++);
      }
    }
    return sorted.map((i) => ({ ...i, order: orderMap.get(i.order) as number }));
  }
}
